import { useState, useEffect } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Phone, WhatsappLogo, ChatText, EnvelopeSimple, PhoneCall } from "@phosphor-icons/react";

export const QUICK_ACTIONS = [
  { key: "call", label: "Call", Icon: Phone, color: "text-orange-600 hover:bg-orange-50" },
  { key: "whatsapp", label: "WhatsApp", Icon: WhatsappLogo, color: "text-emerald-600 hover:bg-emerald-50" },
  { key: "sms", label: "SMS", Icon: ChatText, color: "text-blue-600 hover:bg-blue-50" },
  { key: "email", label: "Email", Icon: EnvelopeSimple, color: "text-violet-600 hover:bg-violet-50" },
  { key: "ivr", label: "IVR", Icon: PhoneCall, color: "text-rose-600 hover:bg-rose-50" },
];

const DUMMY_BODY = {
  whatsapp: "Gentle reminder regarding your overdue EMI. Please pay at the earliest to avoid charges.",
  sms: "Your loan EMI is overdue. Kindly clear the dues to avoid penalties.",
  email: "This is a reminder that your loan account has overdue dues. Please review and pay.",
  ivr: "Automated voice reminder triggered for overdue EMI.",
};

// Replace {name}/{amount}/{loan_id}/{dpd}/{date} placeholders with account values
function renderTemplate(body, acc) {
  if (!body) return "";
  const map = {
    name: acc.borrower_name || "",
    amount: acc.outstanding_amount != null ? `${acc.outstanding_amount}` : "",
    loan_id: acc.loan_id || "",
    dpd: acc.dpd != null ? `${acc.dpd}` : "",
    date: acc.emi_due_date || acc.due_date || "",
    mobile: acc.mobile || "",
  };
  return body.replace(/\{(\w+)\}/g, (m, k) => (map[k] !== undefined ? map[k] : m));
}

export function RowQuickActions({ account, onPick, onLogged }) {
  const [busy, setBusy] = useState(false);
  const fire = async (e, action) => {
    e.stopPropagation();
    if (action.key !== "call") { onPick(account, action.key); return; }
    setBusy(true);
    try {
      await api.post(`/accounts/${account.account_id}/notes`, { note: "Outbound call logged from accounts list.", disposition: "Called" });
      toast.success(`Call logged for ${account.borrower_name}`);
      onLogged && onLogged();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not log Call");
    } finally { setBusy(false); }
  };
  return (
    <div className="flex items-center justify-center gap-0.5">
      {QUICK_ACTIONS.map((a) => (
        <button
          key={a.key}
          title={a.label}
          disabled={busy}
          onClick={(e) => fire(e, a)}
          className={`h-8 w-8 inline-flex items-center justify-center rounded-sm border border-transparent transition-colors disabled:opacity-40 ${a.color}`}
          data-testid={`quick-${a.key}-${account.loan_id}`}
        >
          <a.Icon size={16} weight="bold" />
        </button>
      ))}
    </div>
  );
}

export function CommTemplateDialog({ ctx, templates, onClose, onSent }) {
  const [templateId, setTemplateId] = useState("");
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const open = !!ctx;
  const channel = ctx?.channel;
  const acc = ctx?.account;
  const meta = channel ? (QUICK_ACTIONS.find((a) => a.key === channel) || {}) : {};
  const channelTemplates = (templates || []).filter((t) => t.channel === channel);

  useEffect(() => {
    if (ctx) { setTemplateId(""); setBody(renderTemplate(DUMMY_BODY[ctx.channel], ctx.account)); }
  }, [ctx]);

  const pickTemplate = (id) => {
    setTemplateId(id);
    const tpl = channelTemplates.find((t) => t.id === id);
    setBody(tpl ? renderTemplate(tpl.body, acc) : "");
  };

  const send = async () => {
    if (!body.trim()) return toast.error("Message cannot be empty");
    setSending(true);
    try {
      await api.post(`/communications/send`, { account_id: acc.account_id, channel, body });
      toast.success(`${meta.label} logged for ${acc.borrower_name}`);
      onSent && onSent();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || `Could not log ${meta.label}`);
    } finally { setSending(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-sm" data-testid="quick-comm-dialog">
        <DialogHeader>
          <DialogTitle>Send {meta.label}{acc ? ` — ${acc.borrower_name}` : ""}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Template</Label>
            <Select value={templateId || "custom"} onValueChange={(v) => pickTemplate(v === "custom" ? "" : v)}>
              <SelectTrigger className="rounded-sm" data-testid="quick-template-select"><SelectValue placeholder="Choose template" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="custom">Custom message</SelectItem>
                {channelTemplates.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
              </SelectContent>
            </Select>
            {channelTemplates.length === 0 && <p className="text-[11px] text-muted-foreground mt-1">No saved {meta.label} templates — using a default message.</p>}
          </div>
          <div>
            <Label className="text-xs">Message Preview</Label>
            <Textarea className="rounded-sm min-h-24" value={body} onChange={(e) => setBody(e.target.value)} data-testid="quick-comm-body" />
          </div>
          <p className="text-[11px] text-muted-foreground">This activity is logged to the borrower timeline.</p>
        </div>
        <DialogFooter>
          <Button onClick={send} disabled={sending} className="rounded-sm" data-testid="quick-comm-send">{sending ? "Sending…" : `Send ${meta.label}`}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
